import {
  createProject as publicCreateProject,
  getProject as publicGetProject,
  listProjects as publicListProjects,
  updateProject as publicUpdateProject,
  bulkUpdateProjects as publicBulkUpdateProjects,
  deleteProject as publicDeleteProject,
  queryProjects as publicQueryProjects,
  typedQueryProjects as publicTypedQueryProjects,
  updateProjectOrderInCollection as publicUpdateProjectOrderInCollection,
  queryProjectsWithCollectionInfo as publicQueryProjectsWithCollectionInfo,
} from './portfolio-projects-v1-project-projects.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  ProjectQuery,
  ProjectsQueryBuilder,
  QueryProjectsOptions,
  typedQueryProjects as universalTypedQueryProjects,
} from './portfolio-projects-v1-project-projects.universal.js';
import { onProjectCreated as publicOnProjectCreated } from './portfolio-projects-v1-project-projects.public.js';
import { onProjectDeleted as publicOnProjectDeleted } from './portfolio-projects-v1-project-projects.public.js';
import { onProjectUpdated as publicOnProjectUpdated } from './portfolio-projects-v1-project-projects.public.js';

function customQueryProjects(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: (options?: QueryProjectsOptions) =>
      publicQueryProjects(httpClient)(options),
    typedQueryFunction: (query: ProjectQuery, options?: QueryProjectsOptions) =>
      publicTypedQueryProjects(httpClient)(query, options),
    hasOptionsParameter: true,
  });

  function overloadedQuery(
    query: ProjectQuery,
    options?: QueryProjectsOptions
  ): ReturnType<typeof universalTypedQueryProjects>;
  function overloadedQuery(
    options?: QueryProjectsOptions
  ): ProjectsQueryBuilder;
  function overloadedQuery(
    queryOrOptions?: ProjectQuery | QueryProjectsOptions,
    options?: QueryProjectsOptions
  ): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createProject: MaybeContext<
  BuildRESTFunction<typeof publicCreateProject> & typeof publicCreateProject
> = /*#__PURE__*/ createRESTModule(publicCreateProject);
export const getProject: MaybeContext<
  BuildRESTFunction<typeof publicGetProject> & typeof publicGetProject
> = /*#__PURE__*/ createRESTModule(publicGetProject);
export const listProjects: MaybeContext<
  BuildRESTFunction<typeof publicListProjects> & typeof publicListProjects
> = /*#__PURE__*/ createRESTModule(publicListProjects);
export const updateProject: MaybeContext<
  BuildRESTFunction<typeof publicUpdateProject> & typeof publicUpdateProject
> = /*#__PURE__*/ createRESTModule(publicUpdateProject);
export const bulkUpdateProjects: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateProjects> &
    typeof publicBulkUpdateProjects
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateProjects);
export const deleteProject: MaybeContext<
  BuildRESTFunction<typeof publicDeleteProject> & typeof publicDeleteProject
> = /*#__PURE__*/ createRESTModule(publicDeleteProject);
export const updateProjectOrderInCollection: MaybeContext<
  BuildRESTFunction<typeof publicUpdateProjectOrderInCollection> &
    typeof publicUpdateProjectOrderInCollection
> = /*#__PURE__*/ createRESTModule(publicUpdateProjectOrderInCollection);
export const queryProjectsWithCollectionInfo: MaybeContext<
  BuildRESTFunction<typeof publicQueryProjectsWithCollectionInfo> &
    typeof publicQueryProjectsWithCollectionInfo
> = /*#__PURE__*/ createRESTModule(publicQueryProjectsWithCollectionInfo);
export const queryProjects: MaybeContext<
  BuildRESTFunction<typeof customQueryProjects> & typeof customQueryProjects
> = /*#__PURE__*/ createRESTModule(customQueryProjects);
/**
 * Triggered when a project is created.
 */
export const onProjectCreated: BuildEventDefinition<
  typeof publicOnProjectCreated
> &
  typeof publicOnProjectCreated = createEventModule(publicOnProjectCreated);
/**
 * Triggered when a project is deleted.
 */
export const onProjectDeleted: BuildEventDefinition<
  typeof publicOnProjectDeleted
> &
  typeof publicOnProjectDeleted = createEventModule(publicOnProjectDeleted);
/**
 * Triggered when a project is updated.
 */
export const onProjectUpdated: BuildEventDefinition<
  typeof publicOnProjectUpdated
> &
  typeof publicOnProjectUpdated = createEventModule(publicOnProjectUpdated);

export {
  ImageType,
  SyncStatus,
  Position,
  State,
  SiteCreatedContext,
  Namespace,
  DeleteStatus,
  WebhookIdentityType,
  SortOrder,
} from './portfolio-projects-v1-project-projects.universal.js';
export {
  Project,
  ProjectCoverOneOf,
  Image,
  Point,
  UnsharpMasking,
  Video,
  VideoResolution,
  ProjectDetail,
  ProjectDetailValueOneOf,
  DetailsLink,
  ProjectSource,
  SeoSchema,
  Keyword,
  Tag,
  Settings,
  Watermark,
  MenuSettingUpdatedEvent,
  GetProjectPageDataRequest,
  GetProjectPageDataResponse,
  ProjectSlug,
  CreateNewPortfolioAppRequest,
  CreateNewPortfolioAppResponse,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Asset,
  SiteCreated,
  SiteTransferred,
  SiteDeleted,
  DeleteContext,
  SiteUndeleted,
  SitePublished,
  SiteUnpublished,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  ServiceProvisioned,
  ServiceRemoved,
  SiteRenamed,
  SiteHardDeleted,
  NamespaceChanged,
  StudioAssigned,
  StudioUnassigned,
  SiteUrlChanged,
  SitePurgedExternally,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  WixelAssigned,
  WixelUnassigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  UserDomainMediaEnabled,
  UserDomainMediaDisabled,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  App,
  Page,
  URI,
  File,
  CustomTag,
  Pages,
  URIs,
  SyncProjectWithCollectionMappings,
  DeletedProjectRestored,
  CreateProjectRequest,
  CreateProjectResponse,
  GetProjectRequest,
  GetProjectResponse,
  ListProjectsRequest,
  CursorPaging,
  ListProjectsResponse,
  PagingMetadataV2,
  Cursors,
  UpdateProjectRequest,
  UpdateProjectResponse,
  BulkUpdateProjectsRequest,
  MaskedProject,
  BulkUpdateProjectsResponse,
  BulkUpdateProjectsResult,
  ItemMetadata,
  ApplicationError,
  BulkActionMetadata,
  DeleteProjectRequest,
  DeleteProjectResponse,
  QueryProjectsRequest,
  QueryV2,
  QueryV2PagingMethodOneOf,
  Sorting,
  Paging,
  QueryProjectsResponse,
  UpdateProjectOrderInCollectionRequest,
  UpdateProjectOrderInCollectionResponse,
  ProjectsInCollections,
  QueryProjectWithCollectionInfoRequest,
  QueryProjectWithCollectionInfoResponse,
  RestoreProjectFromTrashBinRequest,
  RestoreProjectFromTrashBinResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  GetAdjacentProjectInfoRequest,
  GetAdjacentProjectInfoResponse,
  ProjectInfo,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  ProjectCreatedEnvelope,
  ProjectDeletedEnvelope,
  ProjectUpdatedEnvelope,
  GetProjectOptions,
  ListProjectsOptions,
  UpdateProject,
  BulkUpdateProjectsOptions,
  QueryProjectsOptions,
  ProjectsQueryResult,
  ProjectsQueryBuilder,
  ProjectQuerySpec,
  UpdateProjectOrderInCollectionIdentifiers,
  QueryProjectsWithCollectionInfoOptions,
} from './portfolio-projects-v1-project-projects.universal.js';
export { utils } from './portfolio-projects-v1-project-projects.universal.js';
export {
  ImageTypeWithLiterals,
  SyncStatusWithLiterals,
  PositionWithLiterals,
  StateWithLiterals,
  SiteCreatedContextWithLiterals,
  NamespaceWithLiterals,
  DeleteStatusWithLiterals,
  WebhookIdentityTypeWithLiterals,
  SortOrderWithLiterals,
  CommonQueryWithEntityContext,
  ProjectQuery,
} from './portfolio-projects-v1-project-projects.universal.js';
